import { cn } from "@repo/ui/lib/utils";
import { Check } from "lucide-react";
import type { Assignment, SessionBookingTarget } from "./types";
import { useScheduleSessionsContext } from "./use-schedule-sessions-context";

/** All sessions with their assignment status — the dialog's persistent left rail. */
export function SessionSidebar() {
  const { state, actions } = useScheduleSessionsContext();
  const { sessions, assignments, activeIdx, mode } = state;

  return (
    <div className="scroll-fade scroll-fade-12 scrollbar-none flex flex-col overflow-y-auto border-r">
      <p className="px-3 pb-1 pt-3 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">
        Sessions
      </p>
      <div className="flex flex-col gap-0.5 px-1.5 pb-2">
        {sessions.map((s, i) => (
          <SidebarItem
            key={s.orderSetId}
            session={s}
            assignment={assignments[s.orderSetId]}
            isActive={activeIdx === i && mode === "scheduling"}
            onClick={() => mode === "scheduling" && actions.switchToGroup(i)}
          />
        ))}
      </div>
    </div>
  );
}

function SidebarItem({
  session,
  assignment,
  isActive,
  onClick,
}: {
  session: SessionBookingTarget;
  assignment: Assignment | undefined;
  isActive: boolean;
  onClick: () => void;
}) {
  const isDone = !!assignment;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full rounded-md px-2.5 py-2 text-left transition-colors",
        isActive ? "bg-primary/5" : "hover:bg-muted",
      )}
    >
      <div className="flex items-start gap-2">
        {/* Status dot */}
        <span
          className={cn(
            "mt-[5px] flex h-2 w-2 shrink-0 items-center justify-center rounded-full transition-colors",
            isDone ? "bg-success" : isActive ? "bg-foreground" : "bg-border",
          )}
        >
          {isDone && <Check className="size-1.5 text-success-foreground" strokeWidth={3} />}
        </span>

        <div className="min-w-0 flex-1">
          <p className={cn("truncate text-xs leading-5", isActive ? "font-medium" : "font-normal")}>
            {session.sessionTitle}
          </p>

          {isDone ? (
            <p className="text-[10px] text-success">
              {assignment.dateLabel} · {assignment.timeLabel}
            </p>
          ) : (
            <p className="text-[10px] text-muted-foreground">
              {session.procedures.length} procedure
              {session.procedures.length !== 1 ? "s" : ""}
              {session.duration != null ? ` · ${session.duration} min` : ""}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
